// src/components/SemanticSearchPanel.js
//
// Firm-scoped semantic search over the user's documents.
// Results are matched by vector similarity against indexed document chunks.
// Each match shows: document title, type, case, similarity score, and the
// matching excerpt. Clicking a result fires `onSelectDocument(documentId)`
// so the parent can open the document in its existing detail/editor flow.

import React, { useState, useMemo } from 'react';
import { semanticSearchDocuments } from '../api';

function SemanticSearchPanel({
  authAxios,
  cases = [],
  documentTypeOptions = [],
  onSelectDocument,
  onToast,
}) {
  const [query, setQuery] = useState('');
  const [docType, setDocType] = useState('');
  const [caseId, setCaseId] = useState('');
  const [topK, setTopK] = useState(10);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [lastQuery, setLastQuery] = useState('');
  const [error, setError] = useState('');

  const caseLookup = useMemo(() => {
    const map = {};
    (cases || []).forEach((c) => { map[c.id] = c; });
    return map;
  }, [cases]);

  const handleSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const q = query.trim();
    if (!q) {
      setError('Enter a search term first.');
      return;
    }
    setError('');
    setIsSearching(true);
    try {
      const data = await semanticSearchDocuments(authAxios, {
        query: q,
        top_k: topK,
        document_type: docType || null,
        case_id: caseId ? parseInt(caseId, 10) : null,
      });
      setResults(data.results || []);
      setLastQuery(data.query || q);
      if ((data.results || []).length === 0 && onToast) {
        onToast('No matches found for that query.', 'info');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Search failed.';
      setError(detail);
      if (onToast) onToast(detail, 'error');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setDocType('');
    setCaseId('');
    setResults([]);
    setLastQuery('');
    setError('');
  };

  const renderScoreBar = (score) => {
    const pct = Math.max(0, Math.min(1, score)) * 100;
    let color = 'bg-gray-400';
    if (score >= 0.85) color = 'bg-green-500';
    else if (score >= 0.7) color = 'bg-blue-500';
    else if (score >= 0.5) color = 'bg-yellow-500';
    return (
      <div className="flex items-center gap-2" title={`Similarity ${pct.toFixed(1)}%`}>
        <div className="w-20 bg-gray-200 rounded-full h-1.5 overflow-hidden">
          <div className={`${color} h-1.5`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-gray-600 font-mono">{pct.toFixed(0)}%</span>
      </div>
    );
  };

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
      <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h5 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <i className="bi bi-search" />
              Semantic Document Search
            </h5>
            <p className="text-xs text-gray-500 mt-1">
              Search across all your firm's documents by meaning, not just keywords.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSearch} className="p-6 space-y-3">
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g. "termination clause notice period" or "deed of transfer signed by..."'
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
            disabled={isSearching}
          />
          <button
            type="submit"
            disabled={isSearching || !query.trim()}
            className="px-5 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSearching ? (
              <span className="inline-flex items-center gap-2">
                <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                Searching…
              </span>
            ) : (
              <span className="inline-flex items-center gap-2"><i className="bi bi-search" /> Search</span>
            )}
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={isSearching}
            className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
          >
            Clear
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm md:w-64"
            disabled={isSearching}
          >
            <option value="">Any document type</option>
            {(documentTypeOptions || []).map((opt) => (
              <option key={opt.value || opt} value={opt.value || opt}>
                {opt.label || opt}
              </option>
            ))}
          </select>
          <select
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm md:w-64"
            disabled={isSearching}
          >
            <option value="">Any case</option>
            {(cases || []).map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
          <select
            value={topK}
            onChange={(e) => setTopK(parseInt(e.target.value, 10) || 10)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm md:w-40"
            disabled={isSearching}
          >
            <option value={5}>Top 5</option>
            <option value={10}>Top 10</option>
            <option value={20}>Top 20</option>
            <option value={50}>Top 50</option>
          </select>
        </div>

        {error && (
          <p className="text-sm text-red-600 mt-1"><i className="bi bi-exclamation-triangle" /> {error}</p>
        )}
      </form>

      {(results.length > 0 || lastQuery) && (
        <div className="px-6 pb-6">
          <div className="flex items-center justify-between mb-3">
            <h6 className="text-sm font-semibold text-gray-700">
              {results.length} result{results.length === 1 ? '' : 's'} for
              <span className="italic text-gray-500"> "{lastQuery}"</span>
            </h6>
          </div>
          <ul className="space-y-2">
            {results.map((r) => (
              <li
                key={`${r.document_id}-${r.chunk_index}`}
                className="border border-gray-200 rounded-md p-4 hover:border-indigo-300 hover:shadow-sm transition cursor-pointer bg-white"
                onClick={() => onSelectDocument && onSelectDocument(r.document_id)}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <h6 className="text-sm font-semibold text-indigo-700 truncate">
                      <i className="bi bi-file-earmark-text" /> {r.document_title}
                    </h6>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
                      {r.document_type && (
                        <span className="inline-block px-2 py-0.5 bg-gray-100 rounded">
                          {r.document_type}
                        </span>
                      )}
                      {r.case_title && (
                        <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                          {r.case_title}
                        </span>
                      )}
                      {!r.case_title && r.case_id && caseLookup[r.case_id] && (
                        <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                          {caseLookup[r.case_id].title}
                        </span>
                      )}
                      <span className="text-gray-400">chunk #{r.chunk_index}</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">{renderScoreBar(r.score)}</div>
                </div>
                <p className="text-sm text-gray-700 line-clamp-3 whitespace-pre-wrap">
                  {r.chunk_text}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SemanticSearchPanel;
